import type { Job } from "../api/types";

/* The engine's reading of an uploaded file, as ingestion/csv_read.py prints
 * it and ingestion/job_worker.py keeps it as result.data, and the record of
 * an import or an undo. Read here, never computed: every outcome in it was
 * decided in the engine, and this file only checks the shape and names the
 * words a person reads for the engine's tokens. */

export type Proposal = {
  header: string;
  target: string;
  source: string;
  confidence: string | null;
  reason: string;
  samples: string[];
  becomes: string[];
};

export type Constant = { field: string; value: string; reason: string; source: string };

export type LookRow = {
  row: number;
  names: Record<string, string>;
  what: { kind: string; result: string; why: string }[];
};

/** Counts per kind of record ("Companies") and outcome ("new"). */
export type Totals = Record<string, Record<string, number>>;

export type ReadData = {
  rows: number;
  columns: number;
  file: string | null;
  says: string;
  /** Which tab of a workbook was read, when other tabs have rows (engine
   *  load_generic_csv.read_file). */
  sheetNote: string | null;
  detectedFormat: string | null;
  proposal: Proposal[];
  relationship: string | null;
  relationshipReason: string | null;
  constants: Constant[];
  /** Values EVIE suggests marking every row with, shown beside the blank
   *  choices and never chosen for the person (engine
   *  csv_read.suggested_values). */
  suggested: { field: string; value: string; reason: string }[];
  model: { asked: boolean; whyNot: string | null; unplaced: number };
  kinds: string[];
  preview: boolean;
  /** How many of the file's first rows the preview rehearsed (engine
   *  csv_read.rehearse, at most the 200 a real load commits at once), or
   *  null for a read made before 2026-10-26, which rehearsed every row. */
  rehearsed: number | null;
  failed: number;
  totals: Totals;
  look: { rows: LookRow[]; total: number };
  /** What EVIE read the unfamiliar values of a column as, by header. */
  values: Record<string, Record<string, string>>;
};

/* sameFileAs: an import of a file loaded before, while that import still
   stands, names it (engine job_worker.settle_same_file, migration 300). Its
   records and its Undo are on that import. */
export type ImportData = {
  signalId: string | null; rows: number; failed: number; look: { rows: LookRow[]; total: number };
  sameFileAs: { jobId: string; loadedAt: string | null } | null;
};

function obj(v: unknown): Record<string, unknown> | null {
  return v && typeof v === "object" && !Array.isArray(v) ? (v as Record<string, unknown>) : null;
}

function str(v: unknown): string | null {
  return typeof v === "string" ? v : null;
}

function num(v: unknown): number {
  return typeof v === "number" ? v : 0;
}

function strings(v: unknown): string[] {
  return Array.isArray(v) ? v.filter((x): x is string => typeof x === "string") : [];
}

function data(job: Job | null | undefined): Record<string, unknown> | null {
  return obj(obj(job?.result)?.["data"]);
}

function look(v: unknown): { rows: LookRow[]; total: number } {
  const o = obj(v);
  const rows: LookRow[] = [];
  for (const r of Array.isArray(o?.["rows"]) ? (o?.["rows"] as unknown[]) : []) {
    const ro = obj(r);
    if (!ro) continue;
    const names: Record<string, string> = {};
    for (const [k, val] of Object.entries(obj(ro["names"]) ?? {})) {
      if (typeof val === "string") names[k] = val;
    }
    const what = (Array.isArray(ro["what"]) ? (ro["what"] as unknown[]) : [])
      .map(obj)
      .filter((w): w is Record<string, unknown> => Boolean(w))
      .map((w) => ({ kind: str(w["kind"]) ?? "", result: str(w["result"]) ?? "", why: str(w["why"]) ?? "" }));
    rows.push({ row: num(ro["row"]), names, what });
  }
  return { rows, total: num(o?.["total"]) };
}

function totals(v: unknown): Totals {
  const out: Totals = {};
  for (const [kind, counts] of Object.entries(obj(v) ?? {})) {
    const c = obj(counts);
    if (!c) continue;
    out[kind] = {};
    for (const [outcome, n] of Object.entries(c)) {
      if (typeof n === "number") out[kind][outcome] = n;
    }
  }
  return out;
}

/** The engine's reading of a file, from a finished file_read job, or null. */
export function readData(job: Job | null | undefined): ReadData | null {
  const d = data(job);
  if (!d || d["kind"] !== "read") return null;
  const proposal: Proposal[] = (Array.isArray(d["proposal"]) ? (d["proposal"] as unknown[]) : [])
    .map(obj)
    .filter((p): p is Record<string, unknown> => Boolean(p))
    .map((p) => ({
      header: str(p["header"]) ?? "",
      target: str(p["target"]) ?? "note",
      source: str(p["source"]) ?? "none",
      confidence: str(p["confidence"]),
      reason: str(p["reason"]) ?? "",
      samples: strings(p["samples"]),
      becomes: strings(p["becomes"]),
    }));
  const constants: Constant[] = (Array.isArray(d["constants"]) ? (d["constants"] as unknown[]) : [])
    .map(obj)
    .filter((c): c is Record<string, unknown> => Boolean(c))
    .map((c) => ({ field: str(c["field"]) ?? "", value: str(c["value"]) ?? "", reason: str(c["reason"]) ?? "", source: str(c["source"]) ?? "" }));
  const model = obj(d["model"]);
  const suggested = (Array.isArray(d["suggested"]) ? (d["suggested"] as unknown[]) : [])
    .map(obj)
    .filter((s): s is Record<string, unknown> => Boolean(s))
    .map((s) => ({ field: str(s["field"]) ?? "", value: str(s["value"]) ?? "", reason: str(s["reason"]) ?? "" }));
  return {
    rows: num(d["rows"]),
    columns: num(d["columns"]),
    file: str(d["file"]),
    says: str(d["says"]) ?? "",
    sheetNote: str(d["sheet_note"]),
    detectedFormat: str(d["detected_format"]),
    proposal,
    relationship: str(d["relationship"]),
    relationshipReason: str(d["relationship_reason"]),
    constants,
    suggested,
    model: { asked: Boolean(model?.["asked"]), whyNot: str(model?.["why_not"]), unplaced: num(model?.["unplaced"]) },
    kinds: strings(d["kinds"]),
    preview: Boolean(d["preview"]),
    rehearsed: d["rehearsed"] === undefined || d["rehearsed"] === null ? null : num(d["rehearsed"]),
    failed: num(d["failed"]),
    totals: totals(d["totals"]),
    look: look(d["look"]),
    values: readings(obj(d["mapping"])?.["values"]),
  };
}

function readings(v: unknown): Record<string, Record<string, string>> {
  const out: Record<string, Record<string, string>> = {};
  for (const [header, words] of Object.entries(obj(v) ?? {})) {
    const w = obj(words);
    if (!w) continue;
    out[header] = {};
    for (const [raw, reads] of Object.entries(w)) {
      if (typeof reads === "string") out[header][raw] = reads;
    }
  }
  return out;
}

/** What a finished import recorded, or null. */
export function importData(job: Job | null | undefined): ImportData | null {
  const d = data(job);
  if (!d || d["kind"] !== "import") return null;
  const same = obj(d["same_file_as"]);
  const sameId = same ? str(same["job_id"]) : null;
  return { signalId: str(d["signal_id"]), rows: num(d["rows"]), failed: num(d["failed"]), look: look(d["look"]),
           sameFileAs: sameId ? { jobId: sameId, loadedAt: str(same?.["loaded_at"]) } : null };
}

/** The mapping the screen sends back: each column's meaning as the engine's
 *  target word, what the company is to the property, and the values added
 *  to every row. The engine turns it into the loader's own form. */
export type Mapping = {
  columns: Record<string, string>;
  constants?: Record<string, string>;
  relationship?: string;
  values?: Record<string, Record<string, string>>;
};

/** What every row of an import can be marked with, in the order a person
 *  reads them. James, 2026-10-23: on the Import screen's form, before the
 *  upload, each optional and blank, for every format. The choices are the
 *  engine's, from api.import_value (engine migration 283). */
export const MARKS = [
  { field: "property.property_type", label: "Property type" },
  { field: "company.role", label: "Company type" },
  { field: "person.role", label: "People's role" },
  { field: "person.seniority", label: "People's seniority" },
] as const;
export const MARK_FIELDS = new Set<string>(MARKS.map((m) => m.field));

/** The marks that are set, as the object the engine takes. */
export function marksSet(marks: Record<string, string>): Record<string, string> {
  return Object.fromEntries(Object.entries(marks).filter(([field, value]) => MARK_FIELDS.has(field) && value));
}

/** The readings go back only for a column whose meaning was not changed on
 *  the screen, because a reading of stage words means nothing for a column
 *  now read as something else. */
export function mappingFrom(targets: Record<string, string>, relationship: string | null, constants: Constant[],
                            read?: ReadData | null): Mapping {
  const m: Mapping = { columns: { ...targets } };
  if (constants.length) m.constants = Object.fromEntries(constants.map((c) => [c.field, c.value]));
  if (relationship) m.relationship = relationship;
  if (read) {
    const kept = Object.entries(read.values).filter(([header]) =>
      read.proposal.some((p) => p.header === header && p.target === targets[header]));
    if (kept.length) m.values = Object.fromEntries(kept);
  }
  return m;
}

/* The words a person reads for the engine's tokens. */

export const SOURCE_WORDS: Record<string, string> = {
  known: "A column EVIE knows",
  saved: "As this shape was last loaded",
  model: "EVIE's reading of the values",
  person: "Chosen here",
  none: "Not placed",
};

export const CONFIDENCE_WORDS: Record<string, string> = { high: "Sure", medium: "Fairly sure", low: "Unsure" };

export const KIND_WORDS: Record<string, string> = {
  person: "People",
  company: "Companies",
  property: "Properties",
  project: "Projects",
  other: "Other",
};

export const ROLE_WORDS: Record<string, string> = {
  owner: "Owner",
  property_manager: "Property manager",
  asset_manager: "Asset manager",
  developer: "Developer",
  general_contractor: "General contractor",
  architect: "Architect",
  none: "Not linked",
};

export const OUTCOME_ORDER = ["new", "matched", "held", "skipped"] as const;
export const OUTCOME_WORDS: Record<string, string> = {
  new: "New",
  matched: "Matched",
  held: "Held for review",
  skipped: "Skipped",
};

/** The vendor formats with their own loaders, as the select offers them. */
export const VENDOR_FORMATS = [
  { key: "zoominfo", label: "ZoomInfo contact export", hint: "The raw CSV, as downloaded." },
  { key: "apollo", label: "Apollo contacts", hint: "The contacts CSV." },
  { key: "yardi", label: "Yardi export", hint: "The workbook." },
  { key: "costar", label: "CoStar property export", hint: "The CSV or the workbook, as downloaded." },
  { key: "lead_sheet", label: "Lead Sheet coming back", hint: "A sheet you sent out and got back." },
] as const;

export const FORMAT_WORDS: Record<string, string> = {
  zoominfo: "ZoomInfo",
  apollo: "Apollo",
  yardi: "Yardi",
  costar: "CoStar",
  lead_sheet: "Lead Sheet",
  generic_csv: "CSV",
};

/** A source name to start from: the file's name without its extension. */
export function sourceFromFile(name: string): string {
  return name.replace(/\.[A-Za-z0-9]+$/, "").replace(/[_]+/g, " ").trim();
}

